import { MessageWrapper } from 'components/messageWrapper/messageWrapper.module';
import { BUTTON_TYPE, CARD_TYPE, CAROUSEL_TYPE } from 'consts/enum';
import {
	addClass,
	generate6DigitUUID,
	getContrastingColor,
	hasClass,
	removeClass,
	removeParent,
} from 'helpers/helpers';
import { Card } from 'ui/card/card.module';
import { initDragListeners } from './carousel.helper';
import { carouselOptions } from './carousel.model';
import { CarouselDotsTemplate, CarouselTemplate } from './carousel.template';
import { CarouselImgPreview } from './carouselImgPreview/carouselImgPreview.module';
import { CarouselList } from './carouselList/carouselList.module';

const {
	SINGLE_INLINE_DOT,
	STACKED_SEPARATED_DOT,
	VERTICAL_LIST,
	VERTICAL_CONTINUOUS_SCROLL_SEAMLESS,
	VERTICAL_SHOW_ONE_SEAMLESS,
	VERTICAL_SHOW_ONE_LIST,
	VERTICAL_SHOW_ONE_SEPARATE,
	VERTICAL_CONTINUOUS_SCROLL_SEPARATE,
	HORIZONTAL_SHOW_ONE_SEAMLESS,
	HORIZONTAL,
	VERTICAL,
} = CAROUSEL_TYPE;

export class Carousel {
	constructor(props) {
		this.initData(props);
		this.initWrapper();
		this.initComponents();
		this.initEventHandlers();
	}

	initData(props) {
		this.id = props?.id ? props?.id : generate6DigitUUID();
		this.data = carouselOptions;
		this.setter({
			...props,
			id: this.id,
		});
		this.dotsId = `engtCarouselDots-${this.id}`;
		this.carouselId = `engtCarousel-${this.id}`;
	}

	initWrapper() {
		const { type, cards, shouldWrap = true } = this.data;
		const wrapper = document.createElement('div');
		let wrapperStyle;
		wrapper.innerHTML = CarouselTemplate(this.data, this.carouselId);
		wrapper.className = 'engt-carousel-wrapper engt-msg-border-bot';
		wrapper.setAttribute('dir', 'ltr');

		if ([VERTICAL_CONTINUOUS_SCROLL_SEAMLESS, VERTICAL_CONTINUOUS_SCROLL_SEPARATE].indexOf(type) > -1) {
			wrapperStyle = 'padding-right: 0px';
		}

		this.html = shouldWrap
			? new MessageWrapper({
					...this.data,
					message: 'carousel',
					innerHTML: wrapper.outerHTML,
					...(wrapperStyle && { style: wrapperStyle }),
			  })
			: wrapper;

		if (cards?.length > 1) {
			const dotWrapper = document.createElement('div');
			dotWrapper.innerHTML = CarouselDotsTemplate(this.data, this.carouselId, this.dotsId);
			dotWrapper.classList.add('engt-carousel-dots');
			dotWrapper.id = this.dotsId;
			this.dots = dotWrapper;
		}
	}

	initComponents() {
		const {
			cards,
			type = VERTICAL_SHOW_ONE_SEAMLESS,
			ellipsis = {},
			config,
			footerBtns,
			headerBtns,
			popoverRole,
			otherChannelsData,
		} = this.data || {};
		const { alignment, width, arrangement } = config || {};
		let buttonType = BUTTON_TYPE.BASE;
		let cardType = CARD_TYPE.SMALL;
		let cardStyle = '';
		let cardsToUse = [];
		const buttonStyle = '';
		this.isCircular = false;
		this.onWrapperClick = null;
		this.bindDragListeners = false;
		this.showInlineDot = false;
		this.showInlineDotArrow = false;
		const ref = this;

		if (config) {
			if (alignment) {
				cardStyle += `text-align: ${alignment};`;
			}
			if (arrangement && arrangement.toLowerCase() === 'flexible') {
				this.html.querySelector(`#${this.carouselId}`).classList.add('engt-carousel-flexible');
			}
		}

		if (
			[VERTICAL_CONTINUOUS_SCROLL_SEAMLESS, VERTICAL_SHOW_ONE_SEAMLESS, HORIZONTAL_SHOW_ONE_SEAMLESS].indexOf(
				type
			) > -1
		) {
			buttonType = BUTTON_TYPE.MINI;
		}

		switch (type) {
			case SINGLE_INLINE_DOT:
				cardType = CARD_TYPE.SMALL;
				cardsToUse = cards?.length > 1 ? [...cards, ...cards] : [...cards];
				break;

			case STACKED_SEPARATED_DOT:
			case HORIZONTAL:
				cardType = CARD_TYPE.SMALL;
				cardStyle = '';
				cardsToUse = cards?.length > 1 ? [...cards, ...cards] : [...cards];
				this.isCircular = true;
				this.bindDragListeners = true;
				this.onWrapperClick = function (e) {
					ref.onStackClick(e, this.parentElement);
				};
				break;

			case VERTICAL:
				cardType = CARD_TYPE.MEDIUM;
				cardsToUse = [...cards];
				cardStyle = 'margin-bottom: 8px;';
				break;

			case VERTICAL_CONTINUOUS_SCROLL_SEAMLESS:
			case VERTICAL_CONTINUOUS_SCROLL_SEPARATE:
				cardType = CARD_TYPE.THIN;
				cardsToUse = cards?.length > 1 ? [...cards, ...cards] : [...cards];
				cardStyle = cards?.length > 1 ? 'margin-bottom: 8px;' : '';
				this.isCircular = true;
				this.bindDragListeners = true;
				this.showInlineDot = true;
				this.showInlineDotArrow = true;
				break;
			case VERTICAL_SHOW_ONE_SEAMLESS:
			case HORIZONTAL_SHOW_ONE_SEAMLESS:
			case VERTICAL_SHOW_ONE_SEPARATE:
				cardType = CARD_TYPE.THIN;
				cardsToUse = [...cards];
				this.bindDragListeners = true;
				this.showInlineDot = true;
				this.showInlineDotArrow = true;
				this.html.querySelector('.engt-carousel-wrapper').style.width = '226px';
				break;

			case VERTICAL_SHOW_ONE_LIST: {
				const wrapper = this.html.querySelector('.engt-carousel-wrapper');
				cardType = CARD_TYPE.THIN;
				cardsToUse = [...cards];
				this.showInlineDot = false;
				this.showInlineDotArrow = false;
				wrapper.style.boxShadow = '0px 0px 12px rgba(64, 63, 66, 0.08)';
				wrapper.appendChild(
					new CarouselList({
						parentHTML: this.html,
						carouselId: this.carouselId,
						footerBtns,
						headerBtns,
						...config,
					})
				);
				break;
			}

			default:
				cardType = CARD_TYPE.SMALL;
				cardsToUse = [...cards];
		}

		cardsToUse.map((card, id) => {
			const { gradient_bottom_color, gradient_top_color, default_action, image_aspect_ratio } = card || {};
			let onClick = null;

			if (gradient_bottom_color && gradient_top_color) {
				cardStyle += `background-image: linear-gradient(to top,${gradient_bottom_color},${gradient_top_color});`;
			}

			cardStyle += `color: ${getContrastingColor(
				gradient_bottom_color || '#ffffff',
				gradient_top_color || '#ffffff'
			)};`;

			if (default_action) {
				onClick = function () {
					window.open(default_action, '_blank');
				};
			}

			this.html.querySelector('cards').appendChild(
				new Card({
					...card,
					id: card.id || id,
					msgId: this.id,
					ellipsis: { ...ellipsis },
					style: cardStyle,
					type: cardType,
					buttonStyle,
					buttonType,
					popoverRole,
					readMoreColor: gradient_bottom_color === gradient_top_color ? gradient_top_color : '',
					onClick,
					onImgClick: this.onCarouselImgClick.bind(ref, id),
					otherChannelsData,
				}).render()
			);
		});

		removeParent(this.html.querySelector('cards'));

		if (this.showInlineDot && this.dots) {
			const div = document.createElement('div');
			div.classList.add('engt-carousel-inline-dot');
			div.appendChild(this.dots);
			this.html.querySelector('.engt-carousel-wrapper').appendChild(div);
		}
	}

	initEventHandlers() {
		const ref = this;
		const { onWrapperClick, showInlineDotArrow, cards, data } = ref;
		const { type } = data;
		const overlay = this.html.querySelector('.engt-carousel-overlay');

		if (onWrapperClick && overlay) {
			overlay.addEventListener('click', onWrapperClick);
		} else if (overlay) {
			overlay.style.display = 'none';
		}

		if (this.dots) {
			const leftArrow = this.dots.querySelector('.arrow-left');
			const rightArrow = this.dots.querySelector('.arrow-right');

			this.dots.querySelectorAll('.dot').forEach((dot, id) => {
				dot.addEventListener('click', () => {
					ref.onCarouselDotClick(ref.carouselId, id);
				});
			});

			if (this.bindDragListeners) {
				initDragListeners.call(this);
			}
			if (showInlineDotArrow) {
				leftArrow.addEventListener('click', (e) => {
					ref.onCarouselPrevious(e, document.getElementById(ref.carouselId));
				});
				rightArrow.addEventListener('click', (e) => {
					ref.onCarouselNext(e, document.getElementById(ref.carouselId));
				});
			} else {
				leftArrow.style.display = 'none';
				rightArrow.style.display = 'none';
			}
		}
	}

	setter(propData) {
		this.data = {
			...this.data,
			...propData,
		};
	}

	setActiveSlide = (elem, currentSlide, slideWidth) => {
		const dotsElem = document.getElementById(this.dotsId);

		elem.querySelectorAll('.engt-card').forEach((card, index) => {
			const left = `${slideWidth * index}px`;
			const isActive = index === currentSlide;
			const isNext = index === currentSlide + 1;

			card.setAttribute('data-active', isActive);
			card.setAttribute('data-next', isNext);
			card.style.left = isActive ? left : '';
		});

		this.setActiveDotAndArrow(dotsElem, currentSlide);
	};

	setActiveDotAndArrow = (dotElems, slideNo) => {
		const dots = dotElems?.querySelectorAll?.('.dot');
		const leftArrow = dotElems.querySelector('.arrow-left');
		const rightArrow = dotElems.querySelector('.arrow-right');

		if (dots?.length) {
			dots.forEach((dotElement, index) => {
				// dot
				if (index === slideNo) {
					addClass(dotElement, 'dot-active');
				} else {
					removeClass(dotElement, 'dot-active');
				}

				// arrow
				if (index === slideNo) {
					if (index === 0 && !this.isCircular) {
						addClass(leftArrow, 'arrow-left-disabled');
					} else {
						removeClass(leftArrow, 'arrow-left-disabled');
					}
					if (index === dots.length - 1 && !this.isCircular) {
						addClass(rightArrow, 'arrow-right-disabled');
					} else {
						removeClass(rightArrow, 'arrow-right-disabled');
					}
				}
			});
		}
	};

	onCarouselPrevious = (event, elem) => {
		if (elem) {
			event.stopPropagation();

			const currentSlide = parseInt(elem.getAttribute('data-active-slide'));
			const totalCount = parseInt(elem.getAttribute('data-slide-length'));
			const selectedSlide = currentSlide === 0 ? totalCount : currentSlide - 1;

			elem.setAttribute('data-active-slide', selectedSlide);
			this.showCard(elem, selectedSlide);
		}
	};

	onCarouselNext = (event, elem) => {
		if (elem) {
			event.stopPropagation();

			const currentSlide = parseInt(elem.getAttribute('data-active-slide'));
			const selectedSlide = currentSlide + 1;

			elem.setAttribute('data-active-slide', selectedSlide);
			this.showCard(elem, selectedSlide);
		}
	};

	onStackClick = (event, elem) => {
		const targetAttribute = parseInt(event.target.getAttribute('id'));
		const activeSlide = parseInt(elem.getAttribute('data-active-slide') || '0');

		if (targetAttribute !== activeSlide) {
			this.onCarouselNext(event, elem);
		}
	};

	onCarouselDotClick = (id, slideNo) => {
		const elem = document.getElementById(id);
		if (elem) {
			elem.setAttribute('data-active-slide', parseInt(slideNo));
			this.showCard(elem, parseInt(slideNo));
		}
	};

	showCard = (elem, cardNo) => {
		if (elem?.children?.length) {
			const totalCount = parseInt(elem.getAttribute('data-slide-length'));
			const slideNo = this.isCircular ? totalCount * 2 - 1 : cardNo;
			let slideWidth = null;
			let xWidth = null;

			if (this.isCircular) {
				slideWidth = elem.querySelectorAll('.engt-card')[slideNo].clientWidth;

				if (
					hasClass(elem, `engt-carousel-${HORIZONTAL.toLowerCase()}`) ||
					hasClass(elem, `engt-carousel-${STACKED_SEPARATED_DOT.toLowerCase()}`)
				) {
					xWidth = slideWidth * cardNo;
				} else {
					xWidth = cardNo ? slideWidth * cardNo + cardNo * 8 - 4 : cardNo;
				}
			} else {
				slideWidth = elem.querySelectorAll('.engt-card')[slideNo].offsetLeft;
				xWidth = slideWidth;
			}

			elem.style.transition = 'transform 0.4s linear';
			elem.style.transform = `translate3d(-${xWidth}px, 0, 0)`;

			this.setActiveSlide(elem, cardNo, slideWidth);

			/* If last slide, smoothly transition to first one (for circular) */
			if (cardNo === totalCount && this.isCircular) {
				setTimeout(() => {
					this.setActiveSlide(elem, 0, slideWidth);
					elem.setAttribute('data-active-slide', 0);
					elem.style.transition = 'unset';
					elem.style.transform = 'translate3d(0px, 0, 0)';
				}, 400);
			}
		}
	};

	getCardImages = function () {
		const { cards = [] } = this.data || {};
		const cardImgs = [];
		cards.map(
			(card, id) =>
				card.image &&
				cardImgs.push({
					url: card.image,
					id,
				})
		);

		return cardImgs;
	};

	onCarouselImgClick = (imgId, e) => {
		const ref = this;
		const cards = this.getCardImages();
		e.preventDefault();
		e.stopPropagation();

		new CarouselImgPreview({
			currentSelected: imgId,
			cardImgs: cards,
			onCardChange(cardId) {
				if (ref.bindDragListeners && cards.length > 1) {
					ref.onCarouselDotClick(ref.carouselId, cardId);
				}
			},
		}).render();
	};

	render() {
		return this.html;
	}
}
